import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface RateLimitResult {
  allowed: boolean;
  remainingAttempts?: number;
  resetTime?: Date;
}

export const useRateLimit = () => {
  const [isChecking, setIsChecking] = useState(false);

  const checkRateLimit = async (
    action: string,
    maxAttempts: number = 5,
    windowMinutes: number = 15
  ): Promise<RateLimitResult> => {
    setIsChecking(true);
    
    try {
      // Use user ID for authenticated users, session-based for anonymous
      const { data: { user } } = await supabase.auth.getUser();
      let identifier: string;
      
      if (user?.id) {
        identifier = user.id;
      } else {
        // For anonymous users, use a combination of user agent and timestamp for basic protection
        // In production, implement proper IP detection server-side
        const sessionId = sessionStorage.getItem('anonymous_session_id') || 
          `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('anonymous_session_id', sessionId);
        identifier = sessionId;
      }
      
      const windowStart = new Date();
      windowStart.setMinutes(windowStart.getMinutes() - windowMinutes);
      
      // Check current rate limit count
      const { data: rateLimitData, error } = await supabase
        .from('rate_limits')
        .select('count, window_start')
        .eq('identifier', identifier)
        .eq('action', action)
        .gte('window_start', windowStart.toISOString())
        .single();

      if (error && error.code !== 'PGRST116') {
        // Error other than "not found"
        console.error('Rate limit check error:', error);
        return { allowed: true }; // Allow on error to avoid blocking legitimate users
      }

      if (!rateLimitData) {
        // No existing rate limit record, create one
        const { error: insertError } = await supabase
          .from('rate_limits')
          .insert({
            identifier,
            action,
            count: 1,
            window_start: new Date().toISOString()
          });
        
        if (insertError) {
          console.error('Rate limit insert error:', insertError);
        }
        
        return { allowed: true, remainingAttempts: maxAttempts - 1 };
      }

      if (rateLimitData.count >= maxAttempts) {
        const resetTime = new Date(rateLimitData.window_start);
        resetTime.setMinutes(resetTime.getMinutes() + windowMinutes);
        
        return { 
          allowed: false, 
          remainingAttempts: 0,
          resetTime 
        };
      }

      // Update count
      const { error: updateError } = await supabase
        .from('rate_limits')
        .update({ count: rateLimitData.count + 1 })
        .eq('identifier', identifier)
        .eq('action', action)
        .eq('window_start', rateLimitData.window_start);

      if (updateError) {
        console.error('Rate limit update error:', updateError);
      }

      return { 
        allowed: true, 
        remainingAttempts: maxAttempts - rateLimitData.count - 1 
      };
      
    } catch (error) {
      console.error('Rate limit check failed:', error);
      return { allowed: true }; // Allow on error to avoid blocking legitimate users
    } finally {
      setIsChecking(false);
    }
  };

  return {
    checkRateLimit,
    isChecking
  };
};